import { observer } from 'mobx-react';
import React from 'react';
import { PromiseMessage } from '../../../lib/components';
import { promiseStates, ReactNodeGenerator } from '../../../lib/helpers';
import { X9StudentComponent } from '../../content/x9/X9StudentComponent';
import { ClassroomHeader } from '../parts/ClassroomHeader';
import { ClassroomLoaderControls } from '../parts/ClassroomLoaderControls';
import { ClassroomVideo } from '../video/ClassroomVideo';
import { StudentClassroomSceneStore } from './StudentClassroomSceneStore';

const ps = promiseStates;

function* renderer(store: StudentClassroomSceneStore): ReactNodeGenerator {
  yield (<ClassroomVideo key="classroom-video"
    store={store.hubStore.video} />);

  yield <ClassroomLoaderControls key="classroom-loader-controls"
    store={store} />;

  if (!store.canConnectToHub)
    return;

  const classroom = store.classroomLoader!.value!;

  yield <ClassroomHeader key="classroom-header"
    store={store.hubStore}
    classroom={classroom}
    isPreview={false} />;


  const snapshotState = store.snapshotState;

  yield (<PromiseMessage key="lesson-loader-state"
    label="Loading lesson" state={snapshotState}
    align="right" />);

  if (ps.isRejected(snapshotState)) {
    yield (
      <div className="row" key="lesson-load-action">
        <div className="col-auto ml-auto">
          <button type="button" className="btn btn-primary"
            onClick={store.loadLesson}>
            Load lesson
          </button>
        </div>
      </div>
    );
  }

  const snapshot = store.snapshot!;
  const classroomState = store.hubStore.state.classroomState!;
  if (snapshot === null || classroomState === null)
    return;

  const currentPage = store.currentPage;
  const currentPushableBlock = store.currentPushableBlock;
  const currentVisibleBlocks = store.currentVisibleBlocks;

  if (currentPage === null || currentPushableBlock === null) {
    yield (
      <div className="row justify-content-center" key="no-current-pushable-block">
        <div className="col-auto">
          <div className="alert alert-info">
            Wait for lesson start
          </div>
        </div>
      </div>
    );
    return;
  }

  yield (
    <div className="row" key="blocks">
      <div className="col">
        <div className="card">
          <div className="card-header">
            Lesson: {snapshot.Name}
            Page: {currentPage.Name}
            Pushable Block: {currentPushableBlock.Name}
          </div>

          <div className="card-body">
            {currentVisibleBlocks.length === 0 &&
              <div className="row justify-content-center">
                <div className="col-auto">
                  <div className="alert alert-info">
                    Empty pushable block
                  </div>
                </div>
              </div>
            }

            {currentVisibleBlocks.map((block) => (
              <div className="card" key={'block-' + block.Id}>
                <div className="card-header">
                  Block
                </div>
                <div className="card-body">
                  {
                    block.ContentSnapshots!.length === 0 &&
                    <div className="row justify-content-center">
                      <div className="col-auto">
                        <div className="alert alert-info">
                          Empty block
                        </div>
                      </div>
                    </div>
                  }
                  {
                    block.ContentSnapshots!.map((content, index) => (
                      <X9StudentComponent key={content.Id}
                                          x9={content}
                                          store={store.hubStore.keyValues}
                                          lessonType={store.lessonType} />
                    ))
                  }
                </div>
              </div>))
            }
          </div>
        </div>
      </div>
    </div>);
}

interface Props {
  id: number;
}

@observer
export class StudentClassroomScene extends React.Component<Props> {
  readonly store: StudentClassroomSceneStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new StudentClassroomSceneStore(props.id);
  }

  componentDidMount() {
    this.store.loadClassroom();
  }

  render() {
    return Array.from(renderer(this.store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
